import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { estagioService, alunoService, empresaService, instituicaoService, seguradoraService, cursoService, agenteIntegradorService } from '@/services/api';
import { Estagio, Aluno, Empresa, Instituicao, Seguradora, Curso, AgenteIntegrador } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { minutosParaHorasMinutos, horasMinutosParaMinutos, formatarInputHorasMinutos } from '@/utils/timeConverter';

const estagioSchema = z.object({
  alunoId: z.number().min(1, 'Aluno é obrigatório'),
  empresaId: z.number().min(1, 'Empresa é obrigatória'),
  instituicaoId: z.number().min(1, 'Instituição é obrigatória'),
  cursoId: z.union([z.number(), z.string().transform(val => val === '' ? undefined : Number(val))]).optional(),
  tipo: z.enum(['Obrigatorio', 'NaoObrigatorio'], {
    required_error: 'Tipo é obrigatório',
  }),
  remunerado: z.boolean(),
  origemInstituicao: z.string().transform(val => val === '' ? undefined : val).optional(),
  dataInicio: z.string().min(1, 'Data de início é obrigatória'),
  dataTermino: z.string().min(1, 'Data de término é obrigatória'),
  cargaHorariaSemanal: z.string()
    .regex(/^\d{1,3}:\d{2}$/, 'Formato deve ser HH:mm (ex: 20:00)')
    .refine((val) => {
      if (!val) return true;
      const minutos = horasMinutosParaMinutos(val);
      return minutos <= 2640; // 44 horas * 60 minutos = 2640 minutos
    }, 'Carga horária semanal não pode ser superior a 44 horas')
    .optional(),
  bolsaAuxilio: z.number().optional(),
  auxilioTransporte: z.number().optional(),
  valeAlimentacao: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || Number.isNaN(val) ? undefined : val),
    z.number().optional()
  ),
  transporteFornecidoEmpresa: z.boolean().default(false),
  seguroApolice: z.string().transform(val => val === '' ? undefined : val).optional(),
  dataEmissao: z.string().transform(val => val === '' ? undefined : val).optional(),
  seguradoraId: z.number().optional(),
  agenteIntegradorId: z.number().optional(),
  status: z.enum(['Ativo', 'Cancelado', 'Concluido'], {
    required_error: 'Status é obrigatório',
  }),
  dataAssinatura: z.string().transform(val => val === '' ? undefined : val).optional(),
  dataCancelamento: z.string().transform(val => val === '' ? undefined : val).optional(),
  motivoEncerramentoId: z.number().optional(),
  possuiResponsavelMenor: z.boolean(),
});

type EstagioFormData = Omit<Estagio, 'id' | 'createdAt' | 'updatedAt' | 'aluno' | 'empresa' | 'instituicao' | 'seguradora' | 'agenteIntegrador' | 'curso' | 'horarios' | 'relatorios' | 'cargaHorariaSemanal'> & {
  cargaHorariaSemanal?: string; // Formato HH:mm no formulário
};

export const NovaEstagioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEstagios } = useQueryInvalidation();

  // Estados para os selects com busca
  const [alunos, setAlunos] = useState<Aluno[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [instituicoes, setInstituicoes] = useState<Instituicao[]>([]);
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [seguradoras, setSeguradoras] = useState<Seguradora[]>([]);
  const [agentesIntegradores, setAgentesIntegradores] = useState<AgenteIntegrador[]>([]);

  // Queries de busca
  const [alunoQuery, setAlunoQuery] = useState<string>("");
  const [empresaQuery, setEmpresaQuery] = useState<string>("");
  const [instituicaoQuery, setInstituicaoQuery] = useState<string>("");
  const [cursoQuery, setCursoQuery] = useState<string>("");
  const [seguradoraQuery, setSeguradoraQuery] = useState<string>("");
  const [agenteIntegradorQuery, setAgenteIntegradorQuery] = useState<string>("");

  // State para carga horária formatada
  const [cargaHorariaValue, setCargaHorariaValue] = useState<string>('00:00');

  // Estados de abertura dos dropdowns
  const [openAluno, setOpenAluno] = useState<boolean>(false);
  const [openEmpresa, setOpenEmpresa] = useState<boolean>(false);
  const [openInstituicao, setOpenInstituicao] = useState<boolean>(false);
  const [openCurso, setOpenCurso] = useState<boolean>(false);
  const [openSeguradora, setOpenSeguradora] = useState<boolean>(false);
  const [openAgenteIntegrador, setOpenAgenteIntegrador] = useState<boolean>(false);

  // Refs para fechamento dos dropdowns
  const alunoRef = useRef<HTMLDivElement | null>(null);
  const empresaRef = useRef<HTMLDivElement | null>(null);
  const instituicaoRef = useRef<HTMLDivElement | null>(null);
  const cursoRef = useRef<HTMLDivElement | null>(null);
  const seguradoraRef = useRef<HTMLDivElement | null>(null);
  const agenteIntegradorRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EstagioFormData>({
    resolver: zodResolver(estagioSchema),
    defaultValues: {
      remunerado: false,
      possuiResponsavelMenor: false,
      transporteFornecidoEmpresa: false,
      status: 'Ativo',
      tipo: 'Obrigatorio',
    },
  });

  const transporteFornecidoEmpresa = watch('transporteFornecidoEmpresa');

  // Buscar dados do estágio (se editando)
  const { data: estagioData, isLoading: loadingEstagio } = useQuery({
    queryKey: ['estagio', id],
    queryFn: () => estagioService.getById(Number(id)),
    enabled: isEditing,
  });

  // Buscar dados para os selects
  const { data: alunosData, isLoading: loadingAlunos } = useQuery({
    queryKey: ['alunos'],
    queryFn: () => alunoService.list({ page: 1, limit: 1000 }),
  });

  const { data: empresasData, isLoading: loadingEmpresas } = useQuery({
    queryKey: ['empresas'],
    queryFn: () => empresaService.list({ page: 1, limit: 1000 }),
  });

  const { data: instituicoesData, isLoading: loadingInstituicoes } = useQuery({
    queryKey: ['instituicoes'],
    queryFn: () => instituicaoService.list({ page: 1, limit: 1000 }),
  });

  const { data: seguradorasData, isLoading: loadingSeguradoras } = useQuery({
    queryKey: ['seguradoras'],
    queryFn: () => seguradoraService.list({ page: 1, limit: 1000 }),
  });

  const { data: agentesIntegradoresData, isLoading: loadingAgentesIntegradores } = useQuery({
    queryKey: ['agentes-integradores'],
    queryFn: () => agenteIntegradorService.list({ page: 1, limit: 1000 }),
  });

  const { data: cursosData, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: () => cursoService.list({ page: 1, limit: 1000 }),
  });

  // Carregar dados dos selects com busca
  useEffect(() => {
    if (alunosData?.data) setAlunos(alunosData.data);
  }, [alunosData]);

  useEffect(() => {
    if (empresasData?.data) setEmpresas(empresasData.data);
  }, [empresasData]);

  useEffect(() => {
    if (instituicoesData?.data) setInstituicoes(instituicoesData.data);
  }, [instituicoesData]);

  useEffect(() => {
    if (cursosData?.data) setCursos(cursosData.data);
  }, [cursosData]);

  useEffect(() => {
    if (seguradorasData?.data) setSeguradoras(seguradorasData.data);
  }, [seguradorasData]);

  useEffect(() => {
    if (agentesIntegradoresData?.data) setAgentesIntegradores(agentesIntegradoresData.data);
  }, [agentesIntegradoresData]);

  // Filtros para os selects com busca
  const filteredAlunos = alunos.filter(a =>
    !alunoQuery ||
    a.nome.toLowerCase().includes(alunoQuery.toLowerCase()) ||
    (a.cpf && a.cpf.includes(alunoQuery))
  );

  const filteredEmpresas = empresas.filter(e =>
    !empresaQuery || e.nomeFantasia.toLowerCase().includes(empresaQuery.toLowerCase())
  );

  const filteredInstituicoes = instituicoes.filter(i =>
    !instituicaoQuery || i.nome.toLowerCase().includes(instituicaoQuery.toLowerCase())
  );

  const filteredCursos = cursos.filter(c =>
    !cursoQuery || c.nome.toLowerCase().includes(cursoQuery.toLowerCase())
  );

  const filteredSeguradoras = seguradoras.filter(s =>
    !seguradoraQuery || s.nome.toLowerCase().includes(seguradoraQuery.toLowerCase())
  );

  const filteredAgentesIntegradores = agentesIntegradores.filter(a =>
    !agenteIntegradorQuery || a.nome.toLowerCase().includes(agenteIntegradorQuery.toLowerCase())
  );

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: any) {
      const target = event.target;
      if (alunoRef.current && target && !alunoRef.current.contains(target)) {
        setOpenAluno(false);
      }
      if (empresaRef.current && target && !empresaRef.current.contains(target)) {
        setOpenEmpresa(false);
      }
      if (instituicaoRef.current && target && !instituicaoRef.current.contains(target)) {
        setOpenInstituicao(false);
      }
      if (cursoRef.current && target && !cursoRef.current.contains(target)) {
        setOpenCurso(false);
      }
      if (seguradoraRef.current && target && !seguradoraRef.current.contains(target)) {
        setOpenSeguradora(false);
      }
      if (agenteIntegradorRef.current && target && !agenteIntegradorRef.current.contains(target)) {
        setOpenAgenteIntegrador(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const createEstagioMutation = useMutation({
    mutationFn: estagioService.create,
    onSuccess: () => {
      invalidateEstagios();
      handleSuccess('Estágio criado com sucesso!');
      navigate('/estagios');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar estágio');
    },
  });

  const updateEstagioMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      estagioService.update(id, data),
    onSuccess: () => {
      invalidateEstagios(id);
      handleSuccess('Estágio atualizado com sucesso!');
      navigate('/estagios');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar estágio');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (estagioData) {
      setValue('alunoId', estagioData.alunoId);
      setValue('empresaId', estagioData.empresaId);
      setValue('instituicaoId', estagioData.instituicaoId);
      setValue('cursoId', estagioData.cursoId);
      setValue('tipo', estagioData.tipo);
      setValue('remunerado', estagioData.remunerado);
      setValue('origemInstituicao', estagioData.origemInstituicao || '');
      setValue('dataInicio', estagioData.dataInicio.split('T')[0]);
      setValue('dataTermino', estagioData.dataTermino.split('T')[0]);

      // Converter minutos para HH:mm
      const cargaHorariaFormatada = minutosParaHorasMinutos(estagioData.cargaHorariaSemanal);
      setCargaHorariaValue(cargaHorariaFormatada);
      setValue('cargaHorariaSemanal', cargaHorariaFormatada);

      setValue('bolsaAuxilio', estagioData.bolsaAuxilio || undefined);
      setValue('auxilioTransporte', estagioData.auxilioTransporte || undefined);
      setValue('valeAlimentacao', estagioData.valeAlimentacao || undefined);
      setValue('transporteFornecidoEmpresa', estagioData.transporteFornecidoEmpresa ?? false);
      setValue('seguroApolice', estagioData.seguroApolice || '');
      setValue('dataEmissao', estagioData.dataEmissao?.split('T')[0] || '');
      setValue('seguradoraId', estagioData.seguradoraId || undefined);
      setValue('agenteIntegradorId', estagioData.agenteIntegradorId || undefined);
      setValue('status', estagioData.status);
      setValue('dataAssinatura', estagioData.dataAssinatura?.split('T')[0] || '');
      setValue('dataCancelamento', estagioData.dataCancelamento?.split('T')[0] || '');
      setValue('motivoEncerramentoId', estagioData.motivoEncerramentoId || undefined);
      setValue('possuiResponsavelMenor', estagioData.possuiResponsavelMenor);

      // Preencher valores iniciais dos selects com busca
      if (estagioData.aluno) {
        setAlunoQuery(estagioData.aluno.nome);
      }
      if (estagioData.empresa) {
        setEmpresaQuery(estagioData.empresa.nomeFantasia);
      }
      if (estagioData.instituicao) {
        setInstituicaoQuery(estagioData.instituicao.nome);
      }
      if (estagioData.seguradora) {
        setSeguradoraQuery(estagioData.seguradora.nome);
      }
      if (estagioData.agenteIntegrador) {
        setAgenteIntegradorQuery(estagioData.agenteIntegrador.nome);
      }

      // Para o curso, buscar na lista de cursos disponíveis pelo cursoId
      if (estagioData.cursoId && cursos.length > 0) {
        const cursoSelecionado = cursos.find(c => c.id === estagioData.cursoId);
        if (cursoSelecionado) {
          setCursoQuery(cursoSelecionado.nome + (cursoSelecionado.nivel ? ` (${cursoSelecionado.nivel})` : ''));
        }
      }
    }
  }, [estagioData, setValue, cursos]);

  const onSubmit = async (data: EstagioFormData) => {
    try {
      setIsSubmitting(true);

      // Converter datas para ISO string e tratar valores vazios
      const formData = {
        ...data,
        dataInicio: new Date(data.dataInicio).toISOString(),
        dataTermino: new Date(data.dataTermino).toISOString(),
        dataAssinatura: data.dataAssinatura ? new Date(data.dataAssinatura).toISOString() : undefined,
        dataCancelamento: data.dataCancelamento ? new Date(data.dataCancelamento).toISOString() : undefined,
        dataEmissao: data.dataEmissao ? new Date(data.dataEmissao).toISOString() : undefined,
        cursoId: data.cursoId || undefined, // Converter string vazia para undefined
        cargaHorariaSemanal: data.cargaHorariaSemanal ? horasMinutosParaMinutos(data.cargaHorariaSemanal) : undefined, // Converter HH:mm para minutos
        // Mantém retrocompatibilidade com versões que ainda dependem do campo legado agregado
        bolsaAuxilio: data.bolsaAuxilio ?? 0,
        auxilioTransporte: data.transporteFornecidoEmpresa ? 0 : (data.auxilioTransporte ?? 0),
        valeAlimentacao:
          data.valeAlimentacao != null && !Number.isNaN(data.valeAlimentacao)
            ? data.valeAlimentacao
            : isEditing
              ? null
              : undefined,
        transporteFornecidoEmpresa: data.transporteFornecidoEmpresa ?? false,
        seguroApolice: data.seguroApolice?.trim() || undefined, // Converter string vazia para undefined
        origemInstituicao: data.origemInstituicao?.trim() || undefined, // Converter string vazia para undefined
        agenteIntegradorId: data.agenteIntegradorId || undefined,
      } as any;

      if (isEditing) {
        await updateEstagioMutation.mutateAsync({ id: Number(id), data: formData });
      } else {
        await createEstagioMutation.mutateAsync(formData);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = loadingEstagio || loadingAlunos || loadingEmpresas || loadingInstituicoes || loadingSeguradoras || loadingAgentesIntegradores || loadingCursos;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Estágio' : 'Novo Estágio'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing
            ? 'Edite as informações do estágio'
            : 'Cadastre um novo estágio no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados Básicos */}
        <Card>
          <CardHeader>
            <CardTitle>Dados Básicos</CardTitle>
            <CardDescription>
              Informações essenciais do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="alunoId">Aluno *</Label>
                <div className="relative" ref={alunoRef}>
                  <input
                    type="text"
                    placeholder="Selecione o aluno"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.alunoId ? 'border-error-500' : 'border-gray-300'
                      }`}
                    value={alunoQuery}
                    onChange={(e) => {
                      setAlunoQuery(e.target.value);
                      setOpenAluno(true);
                    }}
                    onFocus={() => setOpenAluno(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenAluno(false); }}
                  />
                  {alunoQuery && (
                    <button
                      type="button"
                      aria-label="Limpar aluno"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setAlunoQuery("");
                        setValue('alunoId', 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openAluno && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Alunos</div>
                      {filteredAlunos.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setValue('alunoId', a.id);
                            setAlunoQuery(a.nome);
                            setOpenAluno(false);
                          }}
                        >
                          {a.nome} {a.cpf && `(${a.cpf})`}
                        </button>
                      ))}
                      {filteredAlunos.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum aluno encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.alunoId && (
                  <p className="mt-1 text-sm text-error-600">{errors.alunoId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="empresaId">Empresa *</Label>
                <div className="relative" ref={empresaRef}>
                  <input
                    type="text"
                    placeholder="Selecione a empresa"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.empresaId ? 'border-error-500' : 'border-gray-300'
                      }`}
                    value={empresaQuery}
                    onChange={(e) => {
                      setEmpresaQuery(e.target.value);
                      setOpenEmpresa(true);
                    }}
                    onFocus={() => setOpenEmpresa(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenEmpresa(false); }}
                  />
                  {empresaQuery && (
                    <button
                      type="button"
                      aria-label="Limpar empresa"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setEmpresaQuery("");
                        setValue('empresaId', 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openEmpresa && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Empresas</div>
                      {filteredEmpresas.map(e => (
                        <button
                          key={e.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setValue('empresaId', e.id);
                            setEmpresaQuery(e.nomeFantasia);
                            setOpenEmpresa(false);
                          }}
                        >
                          {e.nomeFantasia}
                        </button>
                      ))}
                      {filteredEmpresas.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhuma empresa encontrada</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.empresaId && (
                  <p className="mt-1 text-sm text-error-600">{errors.empresaId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="instituicaoId">Instituição *</Label>
                <div className="relative" ref={instituicaoRef}>
                  <input
                    type="text"
                    placeholder="Selecione a instituição"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.instituicaoId ? 'border-error-500' : 'border-gray-300'
                      }`}
                    value={instituicaoQuery}
                    onChange={(e) => {
                      setInstituicaoQuery(e.target.value);
                      setOpenInstituicao(true);
                    }}
                    onFocus={() => setOpenInstituicao(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenInstituicao(false); }}
                  />
                  {instituicaoQuery && (
                    <button
                      type="button"
                      aria-label="Limpar instituição"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setInstituicaoQuery("");
                        setValue('instituicaoId', 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openInstituicao && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Instituições</div>
                      {filteredInstituicoes.map(i => (
                        <button
                          key={i.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setValue('instituicaoId', i.id);
                            setInstituicaoQuery(i.nome);
                            setOpenInstituicao(false);
                          }}
                        >
                          {i.nome}
                        </button>
                      ))}
                      {filteredInstituicoes.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhuma instituição encontrada</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.instituicaoId && (
                  <p className="mt-1 text-sm text-error-600">{errors.instituicaoId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="cursoId">Curso</Label>
                <div className="relative" ref={cursoRef}>
                  <input
                    type="text"
                    placeholder="Selecione o curso (opcional)"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.cursoId ? 'border-error-500' : 'border-gray-300'
                      }`}
                    value={cursoQuery}
                    onChange={(e) => {
                      setCursoQuery(e.target.value);
                      setOpenCurso(true);
                    }}
                    onFocus={() => setOpenCurso(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenCurso(false); }}
                  />
                  {cursoQuery && (
                    <button
                      type="button"
                      aria-label="Limpar curso"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setCursoQuery("");
                        setValue('cursoId', undefined);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openCurso && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Cursos</div>
                      {filteredCursos.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setValue('cursoId', c.id);
                            setCursoQuery(c.nome + (c.nivel ? ` (${c.nivel})` : ''));
                            setOpenCurso(false);
                          }}
                        >
                          {c.nome} {c.nivel && `(${c.nivel})`}
                        </button>
                      ))}
                      {filteredCursos.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum curso encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.cursoId && (
                  <p className="mt-1 text-sm text-error-600">{errors.cursoId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="tipo">Tipo *</Label>
                <select
                  id="tipo"
                  {...register('tipo')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.tipo ? 'border-error-500' : 'border-gray-300'
                    }`}
                >
                  <option value="Obrigatorio">Obrigatório</option>
                  <option value="NaoObrigatorio">Não Obrigatório</option>
                </select>
                {errors.tipo && (
                  <p className="mt-1 text-sm text-error-600">{errors.tipo.message}</p>
                )}
              </div>

              {/* <div>
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  {...register('status')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.status ? 'border-error-500' : 'border-gray-300'
                    }`}
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Cancelado">Cancelado</option>
                  <option value="Concluido">Concluído</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-error-600">{errors.status.message}</p>
                )}
              </div> */}

              <div>
                <Label htmlFor="origemInstituicao">Origem da Instituição</Label>
                <Input
                  id="origemInstituicao"
                  placeholder="Ex: Curso de Ciência da Computação"
                  {...register('origemInstituicao')}
                  className={errors.origemInstituicao ? 'border-error-500' : ''}
                />
                {errors.origemInstituicao && (
                  <p className="mt-1 text-sm text-error-600">{errors.origemInstituicao.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remunerado"
                  {...register('remunerado')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <Label htmlFor="remunerado" className="ml-2">
                  Estágio remunerado
                </Label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="possuiResponsavelMenor"
                  {...register('possuiResponsavelMenor')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <Label htmlFor="possuiResponsavelMenor" className="ml-2">
                  Possui responsável
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datas */}
        <Card>
          <CardHeader>
            <CardTitle>Datas</CardTitle>
            <CardDescription>
              Período e datas importantes do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dataInicio">Data de Início *</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  {...register('dataInicio')}
                  className={errors.dataInicio ? 'border-error-500' : ''}
                />
                {errors.dataInicio && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataInicio.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataTermino">Data de Término *</Label>
                <Input
                  id="dataTermino"
                  type="date"
                  {...register('dataTermino')}
                  className={errors.dataTermino ? 'border-error-500' : ''}
                />
                {errors.dataTermino && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataTermino.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataAssinatura">Data de Assinatura</Label>
                <Input
                  id="dataAssinatura"
                  type="date"
                  {...register('dataAssinatura')}
                  className={errors.dataAssinatura ? 'border-error-500' : ''}
                />
                {errors.dataAssinatura && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataAssinatura.message}</p>
                )}
              </div>

                <div>
                <Label htmlFor="dataCancelamento">Data de Cancelamento</Label>
                <Input
                  id="dataCancelamento"
                  type="date"
                  {...register('dataCancelamento')}
                  className={errors.dataCancelamento ? 'border-error-500' : ''}
                  disabled
                  readOnly
                />
                {errors.dataCancelamento && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataCancelamento.message}</p>
                )}
                </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalhes Adicionais */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhes Adicionais</CardTitle>
            <CardDescription>
              Informações complementares do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cargaHorariaSemanal">Carga Horária Semanal (HH:mm)</Label>
                <Input
                  id="cargaHorariaSemanal"
                  type="text"
                  placeholder="Ex: 20:00"
                  value={cargaHorariaValue}
                  onChange={(e) => {
                    const formatted = formatarInputHorasMinutos(e.target.value);
                    setCargaHorariaValue(formatted);
                    setValue('cargaHorariaSemanal', formatted);
                  }}
                  className={errors.cargaHorariaSemanal ? 'border-error-500' : ''}
                  maxLength={6}
                />
                {errors.cargaHorariaSemanal && (
                  <p className="mt-1 text-sm text-error-600">{errors.cargaHorariaSemanal.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Formato: HH:mm (ex: 20:00 = 20 horas) - Máximo: 44:00</p>
              </div>

              <div>
                <Label htmlFor="bolsaAuxilio">Valor da bolsa (R$)</Label>
                <Input
                  id="bolsaAuxilio"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 800.00"
                  {...register('bolsaAuxilio', { valueAsNumber: true })}
                  className={errors.bolsaAuxilio ? 'border-error-500' : ''}
                />
                {errors.bolsaAuxilio && (
                  <p className="mt-1 text-sm text-error-600">{errors.bolsaAuxilio.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="auxilioTransporte">Auxílio-transporte (R$)</Label>
                <Input
                  id="auxilioTransporte"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 200.00"
                  disabled={transporteFornecidoEmpresa}
                  {...register('auxilioTransporte', { valueAsNumber: true })}
                  className={errors.auxilioTransporte ? 'border-error-500' : ''}
                />
                {errors.auxilioTransporte && (
                  <p className="mt-1 text-sm text-error-600">{errors.auxilioTransporte.message}</p>
                )}
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="transporteFornecidoEmpresa"
                    {...register('transporteFornecidoEmpresa', {
                      onChange: (e) => {
                        if (e.target.checked) {
                          setValue('auxilioTransporte', undefined);
                        }
                      },
                    })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="transporteFornecidoEmpresa" className="ml-2">
                    Transporte fornecido pela empresa
                  </Label>
                </div>
              </div>

              <div>
                <Label htmlFor="valeAlimentacao">Vale alimentação (R$) <span className="text-gray-400 font-normal">(opcional)</span></Label>
                <Input
                  id="valeAlimentacao"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 150.00"
                  {...register('valeAlimentacao', { valueAsNumber: true })}
                  className={errors.valeAlimentacao ? 'border-error-500' : ''}
                />
                {errors.valeAlimentacao && (
                  <p className="mt-1 text-sm text-error-600">{errors.valeAlimentacao.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="seguroApolice">Apólice do Seguro</Label>
                <Input
                  id="seguroApolice"
                  placeholder="Ex: AP123456"
                  {...register('seguroApolice')}
                  className={errors.seguroApolice ? 'border-error-500' : ''}
                />
                {errors.seguroApolice && (
                  <p className="mt-1 text-sm text-error-600">{errors.seguroApolice.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataEmissao">Data de emissão</Label>
                <Input
                  type="date"
                  id="dataEmissao"
                  {...register('dataEmissao')}
                  className={errors.dataEmissao ? 'border-error-500' : ''}
                />
                {errors.dataEmissao && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataEmissao.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="seguradoraId">Seguradora</Label>
                <div className="relative" ref={seguradoraRef}>
                  <input
                    type="text"
                    placeholder="Selecione a seguradora (opcional)"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.seguradoraId ? 'border-error-500' : 'border-gray-300'
                      }`}
                    value={seguradoraQuery}
                    onChange={(e) => {
                      setSeguradoraQuery(e.target.value);
                      setOpenSeguradora(true);
                    }}
                    onFocus={() => setOpenSeguradora(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenSeguradora(false); }}
                  />
                  {seguradoraQuery && (
                    <button
                      type="button"
                      aria-label="Limpar seguradora"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setSeguradoraQuery("");
                        setValue('seguradoraId', undefined);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openSeguradora && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Seguradoras</div>
                      {filteredSeguradoras.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setValue('seguradoraId', s.id);
                            setSeguradoraQuery(s.nome);
                            setOpenSeguradora(false);
                          }}
                        >
                          {s.nome}
                        </button>
                      ))}
                      {filteredSeguradoras.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhuma seguradora encontrada</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.seguradoraId && (
                  <p className="mt-1 text-sm text-error-600">{errors.seguradoraId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="agenteIntegradorId">Agente Integrador</Label>
                <div className="relative" ref={agenteIntegradorRef}>
                  <input
                    type="text"
                    placeholder="Selecione o agente (opcional)"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.agenteIntegradorId ? 'border-error-500' : 'border-gray-300'
                      }`}
                    value={agenteIntegradorQuery}
                    onChange={(e) => {
                      setAgenteIntegradorQuery(e.target.value);
                      setOpenAgenteIntegrador(true);
                    }}
                    onFocus={() => setOpenAgenteIntegrador(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenAgenteIntegrador(false); }}
                  />
                  {agenteIntegradorQuery && (
                    <button
                      type="button"
                      aria-label="Limpar agente integrador"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setAgenteIntegradorQuery("");
                        setValue('agenteIntegradorId', undefined);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openAgenteIntegrador && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Agentes Integradores</div>
                      {filteredAgentesIntegradores.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setValue('agenteIntegradorId', a.id);
                            setAgenteIntegradorQuery(a.nome);
                            setOpenAgenteIntegrador(false);
                          }}
                        >
                          {a.nome}
                        </button>
                      ))}
                      {filteredAgentesIntegradores.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum agente encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.agenteIntegradorId && (
                  <p className="mt-1 text-sm text-error-600">{errors.agenteIntegradorId.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/estagios')}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Estágio' : 'Criar Estágio')}
          </Button>
        </div>
      </form>
    </div>
  );
};
